import React, { useState } from "react";
import { Pencil, Check, X, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export const EditableField = ({ label, value, display, field, mono = true, type = "text", corrected, onSave, compact = false, align = "left" }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const start = () => { setDraft(value == null ? "" : String(value)); setEditing(true); };
  const commit = async () => {
    setSaving(true);
    try {
      await onSave(field, type === "number" ? Number(draft) : draft.trim());
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={cn("group min-w-0", compact && "inline-block")} data-testid={`field-${field}`}>
      {(label || (corrected && !compact)) && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          {label}
          {corrected && <span className="rounded bg-sky-500/15 px-1 text-[10px] text-sky-400" data-testid={`field-${field}-corrected`} title={`Corrected by ${corrected.by}`}>corrected</span>}
        </div>
      )}
      {editing ? (
        <div className="mt-0.5 flex items-center gap-1">
          <input autoFocus data-testid={`field-${field}-input`} type={type} step={type === "number" ? "0.01" : undefined} value={draft}
                 onChange={(e) => setDraft(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
                 className={cn("h-7 min-w-0 rounded border border-border bg-background px-2 text-sm", compact ? "w-28" : "w-full", mono && "font-mono")} />
          <button data-testid={`field-${field}-save`} onClick={commit} disabled={saving} className="rounded p-1 text-emerald-500 hover:bg-emerald-500/10">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}</button>
          <button data-testid={`field-${field}-cancel`} onClick={() => setEditing(false)} className="rounded p-1 text-muted-foreground hover:bg-muted"><X className="h-3.5 w-3.5" /></button>
        </div>
      ) : (
        <button data-testid={`field-${field}-edit`} onClick={start} title="Click to correct"
                className={cn("flex items-center gap-1.5 text-left break-words rounded hover:bg-muted/60 -mx-1 px-1", compact ? "w-auto text-inherit" : "mt-0.5 w-full text-sm font-medium", align === "right" && "justify-end", mono && "font-mono")}>
          <span className="min-w-0 break-words">{display ?? (value === "" || value == null ? "—" : value)}</span>
          {compact && corrected && <span className="rounded bg-sky-500/15 px-1 text-[10px] text-sky-400" data-testid={`field-${field}-corrected`}>corrected</span>}
          <Pencil className="h-3 w-3 shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
      )}
    </div>
  );
};
